import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import { FormatBasedEditor } from 'tuc-webui-base/components/editors';
import { DurationFormatter } from 'tuc-webui-base/services/Formatters';

const makeEditorParams = (options) => {
  const getJobStartedAt = options.getJobDurationStart || options.jobService.getJobStartedAt.bind(options.jobService);
  const getJobEndedAt = options.getJobDurationEnd || options.jobService.getJobEndedAt.bind(options.jobService);

  const getJobDuration = (job) => {
    const startedAt = getJobStartedAt(job);
    if (!startedAt) return undefined;
    const endedAt = getJobEndedAt(job) || new Date().getTime();
    return endedAt - startedAt;
  };

  const formatterCfg = { unit: 'seconds' };

  const JobDurationFormatter = (e, job) => {
    const d = getJobDuration(job);
    return d ? DurationFormatter(Math.floor(d / 1000), undefined, undefined, formatterCfg) : undefined;
  };

  const JobDurationComponent = ({ object: job, form, label, error, property, editing, clock }) => {
    const timer = useRef(null);
    const startedAt = getJobStartedAt(job);
    const endedAt = getJobEndedAt(job);
    const [now, setNow] = useState(() => new Date().getTime());

    useEffect(() => {
      const clearTimer = () => {
        if (timer.current) {
          if (timer.current.clock) {
            timer.current.clock.unsubscribe(timer.current.sub);
          } else {
            clearInterval(timer.current.interval);
          }
          timer.current = null;
        }
      };
      if (startedAt) {
        if (endedAt) {
          clearTimer();
        } else if (!timer.current) {
          timer.current = {
            clock,
            sub: clock && clock.subscribe((time) => {
              setNow(time);
            }),
            interval: !clock && setInterval(() => {
              setNow(new Date().getTime());
            }),
          };
        }
      }
      return clearTimer;
    }, [startedAt, endedAt, clock, form, editing]);

    const margin = property && property.margin || (form ? 'normal' : 'dense');
    const InputProps = {
      readOnly: true,
      fullWidth: false,
    };

    const fvalue = startedAt && DurationFormatter(
      Math.floor(((endedAt || now) - startedAt) / 1000),
      undefined,
      undefined,
      formatterCfg,
    );

    return (
      <div className={classnames('tuc-textfield-editor', InputProps.readOnly && 'grbrsm-readonly')}>
        {(form || editing) && (
          <TextField
            error={!!error}
            label={label}
            value={fvalue || ' '}
            type="string"
            margin={margin}
            InputProps={InputProps}
            fullWidth={form}
            helperText={error || (form && property ? property.description : undefined)}
          />
        )}
        {!editing && !form && fvalue || ''}
      </div>
    );
  };

  JobDurationComponent.propTypes = {
    object: PropTypes.shape({}).isRequired,
    form: PropTypes.bool,
    label: PropTypes.string,
    error: PropTypes.string,
    property: PropTypes.shape({
      margin: PropTypes.string,
      description: PropTypes.string,
    }),
    editing: PropTypes.bool,
    clock: PropTypes.shape({
      subscribe: PropTypes.func,
      unsubscribe: PropTypes.func,
    }),
  };

  JobDurationComponent.defaultProps = {
    label: '',
    property: undefined,
    form: false,
    error: undefined,
    editing: undefined,
    clock: undefined,
  };

  return [JobDurationFormatter, {
    tableComponent: JobDurationComponent,
    formComponent: JobDurationComponent,
    sortValue: (value, fvalue, job) => (getJobDuration(job) || 0),
  }];
};

export default class JobDurationEditor extends FormatBasedEditor {
  constructor(name, options) {
    super(name || 'jobDuration', ...makeEditorParams(options));
  }
}
